import { useEffect, useState, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { Loader2, Store, Camera, ImageIcon, Trash2, Lock, Sparkles } from 'lucide-react';
import { SaveButton } from '../../components/ui/SaveButton';
import { CollapsibleSection } from '../../components/ui/CollapsibleSection';
import { canUseBankDetails } from '../../lib/tiers';
import { errorMessage, reportError } from '../../lib/errors';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ui/ConfirmProvider';
import { DEFAULT_DOCUMENT_ACCENT } from '../../lib/brandAccent';

interface OrgData {
    name: string;
    // Dedicated identity columns
    contact_name: string;
    business_address: string;
    business_phone: string;
    business_email: string;
    vat_number: string;
    branding: {
        tagline?: string;
        invoice_footer?: string;
        primary_color?: string;
        logo_url?: string;
        invoice_theme?: string;
        invoice_accent_color?: string;
        invoice_due_days?: number;
    };
    settings: {
        thank_you_message?: string;
        google_review_url?: string;
        [key: string]: any;
    };
    // A real column rather than a `settings` key, because the nightly worker
    // filters on it across every org and a jsonb predicate would be the one
    // thing standing between that scan and an index later.
    unreconciled_nag_enabled: boolean;
}

interface BankDetails {
    bank_name: string;
    account_name: string;
    sort_code: string;
    account_number: string;
}

export const ShopIdentitySection = () => {
    const [loading, setLoading] = useState(true);
    const confirm = useConfirm();
    const [uploading, setUploading] = useState(false);
    const [savingBankDetails, setSavingBankDetails] = useState(false);
    const [orgId, setOrgId] = useState<string | null>(null);
    const [tier, setTier] = useState<string>('free');
    const [org, setOrg] = useState<OrgData>({
        name: '',
        contact_name: '',
        business_address: '',
        business_phone: '',
        business_email: '',
        vat_number: '',
        branding: {},
        settings: {},
        unreconciled_nag_enabled: true
    });
    const [bankDetails, setBankDetails] = useState<BankDetails>({
        bank_name: '',
        account_name: '',
        sort_code: '',
        account_number: ''
    });
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const fetchOrg = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            const { data: profile, error: profileError } = await supabase
                .from('profiles')
                .select('active_org_id')
                .eq('id', user.id)
                .single();
            if (profileError) reportError(profileError, 'shop-identity-section.profiles');

            if (!profile?.active_org_id) return;
            setOrgId(profile.active_org_id);

            const { data, error: fetchError } = await supabase
                .from('organizations')
                .select('name, branding, settings, contact_name, business_address, business_phone, business_email, vat_number, subscription_tier, unreconciled_nag_enabled')
                .eq('id', profile.active_org_id)
                .single();
            if (fetchError) reportError(fetchError, 'shop-identity-section.organizations');

            const orgTier = (data as any)?.subscription_tier || 'free';
            setTier(orgTier);

            if (data) {
                setOrg({
                    name: data.name || '',
                    contact_name: data.contact_name || '',
                    business_address: data.business_address || '',
                    business_phone: data.business_phone || '',
                    business_email: data.business_email || '',
                    vat_number: data.vat_number || '',
                    branding: data.branding || {},
                    settings: data.settings || {},
                    // Default ON: the column is NOT NULL DEFAULT true, so an
                    // undefined here means an org row that predates the column.
                    unreconciled_nag_enabled:
                        (data as { unreconciled_nag_enabled?: boolean }).unreconciled_nag_enabled !== false
                });
            }

            // Fetch bank details — paid feature, skip entirely on free tier.
            if (canUseBankDetails(orgTier)) try {
                const { data: { session } } = await supabase.auth.getSession();
                const response = await fetch(
                    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/org-bank-details-fetch?org_id=${profile.active_org_id}`,
                    {
                        headers: {
                            Authorization: `Bearer ${session?.access_token}`
                        }
                    }
                );
                const result = await response.json();
                if (result.bank_details) {
                    setBankDetails(result.bank_details);
                }
            } catch (error) {
                console.error('Failed to fetch bank details:', error);
            }

            setLoading(false);
        };

        fetchOrg();
    }, []);

    const handleLogoClick = () => {
        fileInputRef.current?.click();
    };

    const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !orgId) return;

        if (file.size > 2 * 1024 * 1024) {
            toast.error('Logo must be less than 2MB');
            return;
        }

        setUploading(true);

        try {
            const fileExt = file.name.split('.').pop();
            const fileName = `${orgId}/logo.${fileExt}`;

            const { error: uploadError } = await supabase.storage
                .from('business-logo')
                .upload(fileName, file, { upsert: true });

            if (uploadError) throw uploadError;

            const { data: { publicUrl } } = supabase.storage
                .from('business-logo')
                .getPublicUrl(fileName);

            const logoUrl = `${publicUrl}?t=${Date.now()}`;

            // Update branding JSONB with new logo URL
            const { error: updateError } = await supabase
                .from('organizations')
                .update({
                    branding: { ...org.branding, logo_url: logoUrl }
                })
                .eq('id', orgId);

            if (updateError) throw updateError;

            setOrg(prev => ({
                ...prev,
                branding: { ...prev.branding, logo_url: logoUrl }
            }));
        } catch (error) {
            reportError(error, 'shop-identity.logo-upload');
            toast.error('Failed to upload logo: ' + errorMessage(error));
        } finally {
            setUploading(false);
        }
    };

    const handleSave = async () => {
        if (!orgId) return;

        const { error } = await supabase
            .from('organizations')
            .update({
                name: org.name,
                contact_name: org.contact_name,
                business_address: org.business_address,
                business_phone: org.business_phone,
                business_email: org.business_email,
                vat_number: org.vat_number,
                branding: org.branding,
                settings: org.settings,
                unreconciled_nag_enabled: org.unreconciled_nag_enabled
            })
            .eq('id', orgId);

        if (error) {
            toast.error('Error updating business identity');
            throw error;
        }
    };

    const handleSaveBankDetails = async () => {
        if (!orgId) return;

        // Validation
        if (!bankDetails.bank_name || !bankDetails.account_name || !bankDetails.sort_code || !bankDetails.account_number) {
            toast.error('Please fill in all bank details fields');
            return;
        }

        const sortCodeDigits = bankDetails.sort_code.replace(/\D/g, '');
        if (!/^\d{6}$/.test(sortCodeDigits)) {
            toast.error('Sort code must be 6 digits');
            return;
        }

        if (!/^\d{8}$/.test(bankDetails.account_number)) {
            toast.error('Account number must be 8 digits');
            return;
        }

        setSavingBankDetails(true);

        try {
            const { data: { session } } = await supabase.auth.getSession();
            const response = await fetch(
                `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/org-bank-details-save`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${session?.access_token}`
                    },
                    body: JSON.stringify({
                        org_id: orgId,
                        bank_details: bankDetails
                    })
                }
            );

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || 'Failed to save bank details');
            }

            toast.success('Bank details saved securely');
        } catch (error) {
            toast.error('Error saving bank details: ' + errorMessage(error));
        } finally {
            setSavingBankDetails(false);
        }
    };

    const handleClearBankDetails = async () => {
        if (!await confirm({ title: 'Delete bank details?', message: 'They will stop appearing on your invoices.', confirmText: 'Delete', isDangerous: true })) return;

        if (!orgId) return;

        try {
            const { error } = await supabase
                .from('organizations')
                .update({ bank_details_encrypted: null })
                .eq('id', orgId);

            if (error) throw error;

            setBankDetails({
                bank_name: '',
                account_name: '',
                sort_code: '',
                account_number: ''
            });
            toast.success('Bank details cleared');
        } catch (error) {
            toast.error('Error clearing bank details: ' + errorMessage(error));
        }
    };

    if (loading) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-fg-4" /></div>;

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div>
                <h3 className="text-xl font-bold text-fg flex items-center gap-2">
                    <Store className="w-5 h-5 text-accent" />
                    Business Identity
                </h3>
                <p className="text-sm text-fg-2">Your shop's public-facing details used on invoices and quotes</p>
            </div>

            <div className="space-y-2">
                {/* ── Business Identity ── */}
                <CollapsibleSection title="Business Identity" icon={<Store className="w-4 h-4" />} defaultOpen={false}>
                    <div className="space-y-6">
                        {/* Logo Upload */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-fg-2">Business Logo</label>
                            <div className="flex items-center gap-6">
                                <button
                                    type="button"
                                    onClick={handleLogoClick}
                                    disabled={uploading}
                                    className="relative group w-24 h-24 rounded-[var(--fds-radius)] border-2 border-dashed border-line hover:border-accent bg-raised flex items-center justify-center overflow-hidden transition-all cursor-pointer"
                                >
                                    {uploading ? (
                                        <Loader2 className="w-6 h-6 animate-spin text-accent" />
                                    ) : org.branding.logo_url ? (
                                        <>
                                            <img
                                                src={org.branding.logo_url}
                                                alt="Business Logo"
                                                className="w-full h-full object-contain p-2"
                                            />
                                            {/* Scrim is the desktop hover affordance. On touch it
                                                would just black out the logo, so the corner pip
                                                below stands in — the whole tile is the button
                                                either way. */}
                                            <div className="absolute inset-0 bg-scrim/60 fd-hover-reveal flex items-center justify-center rounded-[var(--fds-radius)]">
                                                <Camera className="w-5 h-5 text-fg" />
                                            </div>
                                            <span
                                                aria-hidden="true"
                                                className="pointer-events-none absolute bottom-1 right-1 w-6 h-6 flex items-center justify-center rounded-full fd-btn-primary shadow lg:hidden"
                                            >
                                                <Camera className="w-3 h-3" />
                                            </span>
                                        </>
                                    ) : (
                                        <div className="flex flex-col items-center gap-1 text-fg-2">
                                            <ImageIcon className="w-6 h-6" />
                                            <span className="text-fd-3xs">Upload</span>
                                        </div>
                                    )}
                                </button>
                                <div className="text-xs text-fg-2 space-y-1">
                                    <p>Click to upload your business logo</p>
                                    <p>PNG, JPG or WebP — max 2MB</p>
                                    <p className="text-accent text-fd-3xs">Appears on invoices, quotes &amp; customer portal</p>
                                </div>
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/png,image/jpeg,image/webp"
                                onChange={handleLogoChange}
                                className="hidden"
                            />
                        </div>

                        {/* Business Name */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-fg-2">Business Name</label>
                            <input
                                type="text"
                                value={org.name}
                                onChange={(e) => setOrg({ ...org, name: e.target.value })}
                                className="w-full bg-raised border border-line rounded-[var(--fds-radius)] px-4 py-2.5 text-fg focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all placeholder:text-fg-2/30"
                                placeholder="e.g. Mike's Motors"
                            />
                        </div>

                        {/* Contact Name */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-fg-2">Contact Person (Full Name)</label>
                            <input
                                type="text"
                                value={org.contact_name}
                                onChange={(e) => setOrg({ ...org, contact_name: e.target.value })}
                                className="w-full bg-raised border border-line rounded-[var(--fds-radius)] px-4 py-2.5 text-fg focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all placeholder:text-fg-2/30"
                                placeholder="e.g. John Doe"
                            />
                        </div>

                        {/* Contact Information */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-fg-2">Contact Email</label>
                                <input
                                    type="email"
                                    value={org.business_email}
                                    onChange={(e) => setOrg({ ...org, business_email: e.target.value })}
                                    className="w-full bg-raised border border-line rounded-[var(--fds-radius)] px-4 py-2.5 text-fg focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all placeholder:text-fg-2/30"
                                    placeholder="e.g. contact@mikesmotors.com"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-fg-2">Contact Phone</label>
                                <input
                                    type="tel"
                                    value={org.business_phone}
                                    onChange={(e) => setOrg({ ...org, business_phone: e.target.value })}
                                    className="w-full bg-raised border border-line rounded-[var(--fds-radius)] px-4 py-2.5 text-fg focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all placeholder:text-fg-2/30"
                                    placeholder="e.g. 07700 900000"
                                />
                            </div>
                        </div>

                        {/* Address */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-fg-2">Business Address</label>
                            <textarea
                                value={org.business_address}
                                onChange={(e) => setOrg({ ...org, business_address: e.target.value })}
                                rows={2}
                                className="w-full bg-raised border border-line rounded-[var(--fds-radius)] px-4 py-2.5 text-fg focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all placeholder:text-fg-2/30 resize-none"
                                placeholder="e.g. 123 Mechanics Way, London, UK"
                            />
                        </div>

                        {/* VAT Number */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-fg-2">VAT Number <span className="text-fg-2/50 font-normal">(optional)</span></label>
                            <input
                                type="text"
                                value={org.vat_number}
                                onChange={(e) => setOrg({ ...org, vat_number: e.target.value })}
                                className="w-full bg-raised border border-line rounded-[var(--fds-radius)] px-4 py-2.5 text-fg focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all placeholder:text-fg-2/30"
                                placeholder="e.g. GB123456789"
                            />
                            <p className="text-xs text-fg-2">Required on invoices if your business is VAT-registered.</p>
                        </div>

                        {/* Tagline */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-fg-2">Tagline</label>
                            <input
                                type="text"
                                value={org.branding.tagline || ''}
                                onChange={(e) => setOrg({ ...org, branding: { ...org.branding, tagline: e.target.value } })}
                                className="w-full bg-raised border border-line rounded-[var(--fds-radius)] px-4 py-2.5 text-fg focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all placeholder:text-fg-2/30"
                                placeholder="e.g. Your trusted local mechanic since 2010"
                            />
                        </div>

                        {/* Invoice Footer */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-fg-2">Invoice Footer Text</label>
                            <textarea
                                value={org.branding.invoice_footer || ''}
                                onChange={(e) => setOrg({ ...org, branding: { ...org.branding, invoice_footer: e.target.value } })}
                                rows={3}
                                className="w-full bg-raised border border-line rounded-[var(--fds-radius)] px-4 py-2.5 text-fg focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all placeholder:text-fg-2/30 resize-none"
                                placeholder="e.g. Thank you for your business! Payment due within 14 days."
                            />
                        </div>

                        {/* Invoice Theme / Color / Due Days */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-fg-2">Invoice Theme</label>
                                <select
                                    value={org.branding.invoice_theme || 'professional'}
                                    onChange={(e) => setOrg({ ...org, branding: { ...org.branding, invoice_theme: e.target.value } })}
                                    className="w-full bg-raised border border-line rounded-[var(--fds-radius)] px-4 py-2.5 text-fg focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all"
                                >
                                    <option value="professional" className="bg-card text-fg">Professional (Default)</option>
                                    <option value="minimal" className="bg-card text-fg">Minimal</option>
                                </select>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-fg-2">Invoice Accent Colour</label>
                                <div className="flex items-center gap-3">
                                    <input
                                        type="color"
                                        value={org.branding.invoice_accent_color || DEFAULT_DOCUMENT_ACCENT}
                                        onChange={(e) => setOrg({ ...org, branding: { ...org.branding, invoice_accent_color: e.target.value } })}
                                        className="w-10 h-10 rounded border-0 cursor-pointer bg-transparent"
                                    />
                                    <div className="flex-1">
                                        <input
                                            type="text"
                                            value={org.branding.invoice_accent_color || DEFAULT_DOCUMENT_ACCENT}
                                            onChange={(e) => setOrg({ ...org, branding: { ...org.branding, invoice_accent_color: e.target.value } })}
                                            className="w-full bg-raised border border-line rounded-[var(--fds-radius)] px-4 py-2.5 text-fg focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all"
                                            placeholder={DEFAULT_DOCUMENT_ACCENT}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-fg-2">Default Invoice Due Terms</label>
                                <div className="flex items-center gap-3">
                                    <select
                                        value={org.branding.invoice_due_days || 14}
                                        onChange={(e) => setOrg({ ...org, branding: { ...org.branding, invoice_due_days: parseInt(e.target.value) } })}
                                        className="w-full md:w-1/2 bg-raised border border-line rounded-[var(--fds-radius)] px-4 py-2.5 text-fg focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all"
                                    >
                                        <option value={7} className="bg-card text-fg">7 Days</option>
                                        <option value={14} className="bg-card text-fg">14 Days (Default)</option>
                                        <option value={30} className="bg-card text-fg">30 Days</option>
                                        <option value={60} className="bg-card text-fg">60 Days</option>
                                        <option value={90} className="bg-card text-fg">90 Days</option>
                                    </select>
                                    <p className="text-xs text-fg-2 flex-1">
                                        New invoices will automatically set their due date based on this term when issued.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </CollapsibleSection>

                {/* ── Bank Payment Details ── */}
                <CollapsibleSection title="Bank Payment Details" icon={<Lock className="w-4 h-4" />} defaultOpen={false}>
                    {!canUseBankDetails(tier) ? (
                        <div className="flex flex-col items-center text-center gap-3 bg-raised border border-accent/20 p-6 rounded-[var(--fds-radius)]">
                            <div className="w-11 h-11 rounded-full bg-accent/10 flex items-center justify-center">
                                <Sparkles className="w-5 h-5 text-accent" />
                            </div>
                            <div>
                                <p className="text-sm font-semibold text-fg">Upgrade to add bank details</p>
                                <p className="text-xs text-fg-2 mt-1 max-w-sm">
                                    Show your bank account on invoices so customers can pay you by transfer.
                                    Available on Pro and above — upgrade in Settings &gt; Subscription.
                                </p>
                            </div>
                        </div>
                    ) : (
                    <div className="space-y-4 bg-raised p-4 rounded-[var(--fds-radius)]">
                        <p className="text-xs text-fg-2 mb-4">
                            <Lock className="w-3 h-3 inline mr-2" />
                            These details are encrypted and only shown on invoices when Stripe is not connected or verified.
                        </p>

                        {/* Bank Name */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-fg-2">Bank Name</label>
                            <input
                                type="text"
                                value={bankDetails.bank_name}
                                onChange={(e) => setBankDetails({ ...bankDetails, bank_name: e.target.value })}
                                className="w-full bg-canvas border border-line rounded-[var(--fds-radius)] px-4 py-2.5 text-fg focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all placeholder:text-fg-2/30"
                                placeholder="e.g. Barclays Bank, NatWest"
                            />
                        </div>

                        {/* Account Name */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-fg-2">Account Holder</label>
                            <input
                                type="text"
                                value={bankDetails.account_name}
                                onChange={(e) => setBankDetails({ ...bankDetails, account_name: e.target.value })}
                                className="w-full bg-canvas border border-line rounded-[var(--fds-radius)] px-4 py-2.5 text-fg focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all placeholder:text-fg-2/30"
                                placeholder="Business name or account holder name"
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Sort Code */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-fg-2">Sort Code</label>
                                <input
                                    type="text"
                                    value={bankDetails.sort_code}
                                    onChange={(e) => {
                                        let val = e.target.value.replace(/\D/g, '').slice(0, 6);
                                        if (val.length > 2) val = val.slice(0, 2) + '-' + val.slice(2);
                                        if (val.length > 5) val = val.slice(0, 5) + '-' + val.slice(5);
                                        setBankDetails({ ...bankDetails, sort_code: val });
                                    }}
                                    className="w-full bg-canvas border border-line rounded-[var(--fds-radius)] px-4 py-2.5 text-fg focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all placeholder:text-fg-2/30 font-mono"
                                    placeholder="XX-XX-XX"
                                    maxLength={8}
                                />
                            </div>

                            {/* Account Number */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-fg-2">Account Number</label>
                                <input
                                    type="text"
                                    value={bankDetails.account_number}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/\D/g, '').slice(0, 8);
                                        setBankDetails({ ...bankDetails, account_number: val });
                                    }}
                                    className="w-full bg-canvas border border-line rounded-[var(--fds-radius)] px-4 py-2.5 text-fg focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all placeholder:text-fg-2/30 font-mono"
                                    placeholder="XXXXXXXX"
                                    maxLength={8}
                                />
                            </div>
                        </div>

                        <div className="flex gap-3 pt-2">
                            <button
                                type="button"
                                onClick={handleSaveBankDetails}
                                disabled={savingBankDetails}
                                className="flex-1 fd-btn-primary px-4 py-2.5 rounded-[var(--fds-radius)] font-medium disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                            >
                                {savingBankDetails && <Loader2 className="w-4 h-4 animate-spin" />}
                                Save Bank Details
                            </button>
                            {bankDetails.bank_name && (
                                <button
                                    type="button"
                                    onClick={handleClearBankDetails}
                                    className="px-4 py-2.5 rounded-[var(--fds-radius)] border border-danger/30 text-danger hover:bg-danger/10 transition-all flex items-center gap-2"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    Clear
                                </button>
                            )}
                        </div>
                    </div>
                    )}
                </CollapsibleSection>

                {/* ── Customer Experience ── */}
                <CollapsibleSection title="Customer Experience" defaultOpen={false}>
                    <div className="space-y-6">
                        {/* Thank You Message */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-fg-2">Payment Thank You Message</label>
                            <textarea
                                value={org.settings.thank_you_message || ''}
                                onChange={(e) => setOrg({ ...org, settings: { ...org.settings, thank_you_message: e.target.value } })}
                                rows={3}
                                className="w-full bg-raised border border-line rounded-[var(--fds-radius)] px-4 py-2.5 text-fg focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all placeholder:text-fg-2/30 resize-none"
                                placeholder="e.g. Thanks for choosing us — drive safe!"
                                maxLength={200}
                            />
                            <p className="text-xs text-fg-2">
                                This message appears on the customer's payment confirmation page.
                            </p>
                        </div>

                        {/* Google Review URL */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-fg-2">Google Review Link</label>
                            <input
                                type="url"
                                value={org.settings.google_review_url || ''}
                                onChange={(e) => setOrg({ ...org, settings: { ...org.settings, google_review_url: e.target.value } })}
                                className="w-full bg-raised border border-line rounded-[var(--fds-radius)] px-4 py-2.5 text-fg focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all placeholder:text-fg-2/30"
                                placeholder="https://g.page/r/YOUR_REVIEW_LINK/review"
                            />
                            <p className="text-xs text-fg-2">
                                Paste your Google Business review link. A "Leave a Review" button will appear on the payment confirmation page.
                            </p>
                        </div>

                        {/* MOT reminder master switch */}
                        <div className="space-y-2 pt-4 border-t border-line">
                            <div className="flex items-center justify-between gap-4">
                                <div>
                                    <label className="text-sm font-medium text-fg">Automatic MOT reminders</label>
                                    <p className="text-xs text-fg-2 mt-0.5 max-w-md">
                                        Email customers before their vehicle's MOT is due (about a month out, then a week out). Turn off to stop all MOT reminder emails for your business. You can still disable individual vehicles on their profile.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={org.settings.mot_reminders_enabled !== false}
                                    onClick={() => setOrg({ ...org, settings: { ...org.settings, mot_reminders_enabled: !(org.settings.mot_reminders_enabled !== false) } })}
                                    aria-label="Automatic MOT reminders"
                                    className="fd-switch"
                                />
                            </div>
                        </div>

                        {/* Missing parts invoice reminders.
                            Turning this off silences the notifications and the emails.
                            It deliberately does NOT hide the Needs Attention row on the
                            dashboard: that row is the honest state of the books, and a
                            reminder setting should not be able to make money you owe tax
                            on disappear from view. */}
                        <div className="space-y-2 pt-4 border-t border-line">
                            <div className="flex items-center justify-between gap-4">
                                <div>
                                    <label className="text-sm font-medium text-fg">Missing parts invoice reminders</label>
                                    <p className="text-xs text-fg-2 mt-0.5 max-w-md">
                                        Nudge you when paid jobs have parts on them but no supplier invoice recorded, so you don't end up paying tax on money you never kept. Starts after a week, then gets more insistent. Turn off to stop the notifications and emails — the reminder on your dashboard stays either way.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={org.unreconciled_nag_enabled}
                                    onClick={() => setOrg({ ...org, unreconciled_nag_enabled: !org.unreconciled_nag_enabled })}
                                    aria-label="Missing parts invoice reminders"
                                    className="fd-switch"
                                />
                            </div>
                        </div>
                    </div>
                </CollapsibleSection>

                <div className="pt-4">
                    <SaveButton onClick={handleSave} />
                </div>
            </div>
        </div>
    );
};
